import { FARE_ENGINE_API_BASE } from "@/config/apiConfig";
import { publicAuthorizedFetch } from "@/services/publicAuthorizedFetch";
import type {
  CalculateSeatFareResponse,
  FareCalculationRequest,
  FuelType,
} from "@/types/fareCalculation";

const DEFAULT_FARE_REQUEST: Omit<
  FareCalculationRequest,
  | "origin"
  | "destination"
  | "availableSeats"
  | "departureTime"
  | "carCompany"
  | "carModel"
  | "fuelType"
> = {
  carType: "Hatchback",
  carYear: 0,
  carTag: "Not Specified",
  hasAC: true,
  hasCharging: true,
  hasWifi: true,
  hasMusicSystem: true,
  hasClimateControl: true,
  hasPremiumSoundSystem: true,
  seatType: "Fabric",
  airBags: 0,
  hasRecliner: true,
  hasVideoScreens: true,
  tripPurpose: "Personal",
};

const MIN_DEPARTURE_MINUTES = 40;
export { prewarmFareEngine } from './prewarmService';

function getDefaultDepartureTime(): string {
  const departure = new Date();
  departure.setMinutes(departure.getMinutes() + MIN_DEPARTURE_MINUTES);
  return departure.toISOString();
}

export function buildFareCalculationRequest(params: {
  origin: string;
  destination: string;
  availableSeats: number;
  carCompany: string;
  carModel: string;
  fuelType: FuelType;
  departureTime?: string;
}): FareCalculationRequest {
  return {
    ...DEFAULT_FARE_REQUEST,
    origin: params.origin,
    destination: params.destination,
    availableSeats: params.availableSeats,
    departureTime: params.departureTime ?? getDefaultDepartureTime(),
    carCompany: params.carCompany,
    carModel: params.carModel,
    fuelType: params.fuelType,
  };
}

export async function calculateSeatFare(
  request: FareCalculationRequest
): Promise<CalculateSeatFareResponse> {
  const res = await publicAuthorizedFetch(
    `${FARE_ENGINE_API_BASE}/api/FareEngineCalculator/calculate-seat-fare`,
    {
      method: "POST",
      headers: {
        accept: "*/*",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    }
  );

  if (!res.ok) {
    throw new Error(`Fare calculation failed with HTTP ${res.status}`);
  }

  const json = (await res.json()) as CalculateSeatFareResponse;
  if (json.status !== "success" || !json.fareCalculation) {
    throw new Error("Fare calculation returned an unexpected response structure");
  }

  return json;
}
